import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Card,
  message,
  Modal,
  Input,
  Typography,
  Row,
  Col,
  Spin,
  Tag,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { NavLink } from "react-router-dom";
import axios from "axios";
import CustomerAgreementForm from "./customerAgreementForm";

const { Search } = Input;
const { Title } = Typography;

const CustomerAgreementList = () => {
  const [agreements, setAgreements] = useState([]);
  const [filteredAgreements, setFilteredAgreements] = useState([]);
  const [formData, setFormData] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    fetchAgreements();
  }, []);

  const fetchAgreements = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        "http://93.127.203.113:5005/customer-agreements"
      );
      setAgreements(response.data);
      setFilteredAgreements(response.data);
    } catch (error) {
      message.error("Failed to fetch agreements.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    const lowerSearch = value.toLowerCase();
    const filtered = agreements.filter((record) => {
      const fieldsToSearch = [
        `${record.passenger?.firstName || ""} ${
          record.passenger?.lastName || ""
        }`,
        record.vehicle?.plateNumber || "",
        record.pricePerDay?.toString() || "",
        new Date(record.startDate).toLocaleDateString(),
        new Date(record.endDate).toLocaleDateString() || "N/A",
      ];
      return fieldsToSearch.some((field) =>
        field.toLowerCase().includes(lowerSearch)
      );
    });
    setFilteredAgreements(filtered);
  };

  const highlightText = (text, searchText) => {
    if (!searchText) return text;
    const regex = new RegExp(`(${searchText})`, "gi");
    const parts = text.toString().split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "#fffb8f",
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const handleEdit = (record) => {
    setFormData(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await axios.delete(`http://93.127.203.113:5005/customer-agreements/${id}`);
      message.success("Agreement deleted successfully.");
      fetchAgreements();
    } catch (error) {
      message.error("Failed to delete agreement.");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: <span style={{ fontWeight: "bold" }}>Passenger Name</span>,
      dataIndex: "passenger",
      key: "passengerName",
      render: (passenger) =>
        highlightText(
          passenger ? `${passenger.firstName} ${passenger.lastName}` : "N/A",
          searchText
        ),
    },
    {
      title: <span style={{ fontWeight: "bold" }}>Vehicle</span>,
      dataIndex: "vehicle",
      key: "vehicle",
      render: (vehicle) =>
        highlightText(
          vehicle ? `${vehicle.plateNumber} (${vehicle.model})` : "N/A",
          searchText
        ),
    },

    {
      title: <span style={{ fontWeight: "bold" }}>Price Per Day</span>,
      dataIndex: "pricePerDay",
      key: "pricePerDay",
      render: (price) => highlightText(`$${price}`, searchText),
    },
    {
      title: <span style={{ fontWeight: "bold" }}>Start Date</span>,
      dataIndex: "startDate",
      key: "startDate",
      render: (date) =>
        highlightText(new Date(date).toLocaleDateString() || "N/A", searchText),
    },
    {
      title: <span style={{ fontWeight: "bold" }}>End Date</span>,
      dataIndex: "endDate",
      key: "endDate",
      render: (date) =>
        highlightText(
          date ? new Date(date).toLocaleDateString() : "N/A",
          searchText
        ),
    },

    {
      title: <span style={{ fontWeight: "bold" }}>Inspection Status</span>,
      dataIndex: "inspection",
      key: "inspectionStatus",
      render: (inspection) =>
        inspection ? (
          <Tag
            color={
              inspection.status === "Active" ? "green" : "red"
            }
            style={{ fontWeight: "bold" }}
          >
            {highlightText(inspection.status, searchText)}
          </Tag>
        ) : (
          "N/A"
        ),
    },
    {
      title: <span style={{ fontWeight: "bold" }}>Actions</span>,
      key: "actions",
      render: (_, record) => (
        <>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: "#0c8c49", marginRight: "8px" }}
          >
            Edit
          </Button>
          {/* <Button
            type="link"
            danger
            onClick={() => handleDelete(record.agreementId)}
          >
            Delete
          </Button> */}
          <Button type="link">
            <NavLink
              to={`/customerAgreementDetails/${record.agreementId}`}
              style={{ color: "#0c8c49" }}
            >
              <InfoCircleOutlined /> Details
            </NavLink>
          </Button>
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <Card
        style={{
          borderRadius: "10px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          borderTop: "5px solid #0c8c49",
        }}
      >
        <Title level={4} style={{ color: "#0c8c49", marginBottom: "20px" }}>
          Customer Agreements
        </Title>
        <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
          <Col span={18}>
            <Search
              placeholder="Search Agreements"
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={handleSearch}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: "60%" }}
            />
          </Col>
          <Col span={6} style={{ textAlign: "right" }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setFormData({});
                setModalVisible(true);
              }}
              style={{
                backgroundColor: "#0c8c49",
                borderColor: "#0c8c49",
              }}
            >
              Add Agreement
            </Button>
          </Col>
        </Row>
        {loading ? (
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={filteredAgreements}
            rowKey="agreementId"
            pagination={{ pageSize: 5 }}
            bordered
          />
        )}
      </Card>

      <Modal
        title={formData.agreementId ? "Edit Agreement" : "Add Agreement"}
        visible={modalVisible}
        footer={null}
        onCancel={() => setModalVisible(false)}
        centered
        width="50%"
        style={{ borderRadius: "10px" }}
        bodyStyle={{
          padding: "20px",
          borderRadius: "10px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <CustomerAgreementForm
          formData={formData}
          setFormData={setFormData}
          closeModal={() => setModalVisible(false)}
          refetchData={fetchAgreements}
        />
      </Modal>
    </div>
  );
};

export default CustomerAgreementList;
